import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FlagsContext } from 'src/context/flagsContext';
import { useMediaQuery } from 'src/hooks/useMediaQuery';

export const DRAWER_IDS = {
  INTERNAL_CHAT: 'INTERNAL_CHAT',
  NOTES: 'NOTES',
  DETAILS: 'DETAILS',
} as const;

export type DrawerId = (typeof DRAWER_IDS)[keyof typeof DRAWER_IDS];

// CLEANUP(EnableContextBarVisualUpgrades): Keep these when flag is removed
export const DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Internal Chat',
  [DRAWER_IDS.NOTES]: 'Internal Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export const MOBILE_DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Chat',
  [DRAWER_IDS.NOTES]: 'Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export interface DrawerActionConfig {
  icon: string;
  label: string;
  actionType: 'create_note';
}

export const DRAWER_ACTION_CONFIGS: Partial<
  Record<DrawerId, DrawerActionConfig>
> = {
  [DRAWER_IDS.NOTES]: {
    icon: 'Plus',
    label: 'Create note',
    actionType: 'create_note',
  },
};

export interface ContextBarTarget {
  id: string;
  entityType: 'client' | 'company';
}

export type DrawerVisibilityConfig = Partial<Record<DrawerId, boolean>>;

const DEFAULT_DRAWER_VISIBILITY: Record<DrawerId, boolean> = {
  [DRAWER_IDS.INTERNAL_CHAT]: false,
  [DRAWER_IDS.NOTES]: true,
  [DRAWER_IDS.DETAILS]: true,
};

export interface ContextBarPageTransition {
  target: ContextBarTarget;
  drawerConfig?: DrawerVisibilityConfig;
  open?: boolean;
}

interface ContextBarState {
  target?: ContextBarTarget;
  drawerVisibilityConfig: Record<DrawerId, boolean>;
  lastOpenDrawer?: DrawerId;
  drawerHistory: DrawerId[];
  open: boolean;
  userOpenPreference?: boolean;
}

export interface ContextBarContextValue extends ContextBarState {
  transition: (transition: ContextBarPageTransition) => void;
  setLastOpenDrawer: (drawerId: DrawerId) => void;
  pushDrawerHistory: (drawerId: DrawerId) => void;
  getActiveDrawer: (hasAssistantTab: boolean) => DrawerId;
  setOpen: (value: boolean) => void;
  isMobileLayout: boolean;
}

const defaultContextValue: ContextBarContextValue = {
  target: undefined,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  lastOpenDrawer: undefined,
  drawerHistory: [],
  open: false,
  userOpenPreference: undefined,
  transition: () => {},
  setLastOpenDrawer: () => {},
  pushDrawerHistory: () => {},
  getActiveDrawer: () => DRAWER_IDS.INTERNAL_CHAT,
  setOpen: () => {},
  isMobileLayout: false,
};

export const ContextBarContext =
  createContext<ContextBarContextValue>(defaultContextValue);

const initialState: ContextBarState = {
  target: undefined,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  lastOpenDrawer: undefined,
  drawerHistory: [],
  open: false,
  userOpenPreference: undefined,
};

export const ContextBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<ContextBarState>(initialState);
  const { EnableContextBarOtherPage } = useContext(FlagsContext);

  const isMobileLayout = useMediaQuery('sm', 'max');

  const setOpen = useCallback(
    (value: boolean) =>
      setState((prev) => ({ ...prev, open: value, userOpenPreference: value })),
    [setState],
  );

  const transition = useCallback(
    (transitionObj: ContextBarPageTransition) => {
      setState((prev) => {
        let nextOpen = false;
        if (!isMobileLayout) {
          if (transitionObj.open !== undefined) {
            nextOpen = transitionObj.open;
          } else if (prev.userOpenPreference !== undefined) {
            nextOpen = prev.userOpenPreference;
          }
        }

        return {
          ...prev,
          target: transitionObj.target,
          drawerVisibilityConfig: {
            ...DEFAULT_DRAWER_VISIBILITY,
            ...Object.fromEntries(
              Object.entries(transitionObj.drawerConfig ?? {}).filter(
                ([, v]) => v !== undefined,
              ),
            ),
          },
          open: nextOpen,
        };
      });
    },
    [isMobileLayout, setState],
  );

  const setLastOpenDrawer = useCallback(
    (drawerId: DrawerId) => {
      setState((prev) => {
        if (EnableContextBarOtherPage) {
          const filtered = prev.drawerHistory.filter((id) => id !== drawerId);
          return { ...prev, drawerHistory: [drawerId, ...filtered] };
        }
        return { ...prev, lastOpenDrawer: drawerId };
      });
    },
    [EnableContextBarOtherPage],
  );

  const pushDrawerHistory = useCallback((drawerId: DrawerId) => {
    setState((prev) => {
      const filtered = prev.drawerHistory.filter((id) => id !== drawerId);
      return { ...prev, drawerHistory: [drawerId, ...filtered] };
    });
  }, []);

  const isDrawerValid = useCallback(
    (drawerId: DrawerId, hasAssistantTab: boolean): boolean => {
      if (!state.drawerVisibilityConfig[drawerId]) {
        return false;
      }
      if (drawerId === DRAWER_IDS.INTERNAL_CHAT && !hasAssistantTab) {
        return false;
      }
      return true;
    },
    [state.drawerVisibilityConfig],
  );

  const getActiveDrawer = useCallback(
    (hasAssistantTab: boolean): DrawerId => {
      if (EnableContextBarOtherPage) {
        for (const drawerId of state.drawerHistory) {
          if (isDrawerValid(drawerId, hasAssistantTab)) {
            return drawerId;
          }
        }
      } else {
        if (
          state.lastOpenDrawer &&
          isDrawerValid(state.lastOpenDrawer, hasAssistantTab)
        ) {
          return state.lastOpenDrawer;
        }
      }
      return hasAssistantTab && state.drawerVisibilityConfig.INTERNAL_CHAT
        ? DRAWER_IDS.INTERNAL_CHAT
        : DRAWER_IDS.NOTES;
    },
    [
      EnableContextBarOtherPage,
      state.lastOpenDrawer,
      state.drawerHistory,
      state.drawerVisibilityConfig.INTERNAL_CHAT,
      isDrawerValid,
    ],
  );

  const value = useMemo(
    () =>
      ({
        ...state,
        transition,
        setLastOpenDrawer,
        pushDrawerHistory,
        getActiveDrawer,
        setOpen,
        isMobileLayout,
      }) satisfies ContextBarContextValue,
    [
      state,
      transition,
      setLastOpenDrawer,
      pushDrawerHistory,
      getActiveDrawer,
      setOpen,
      isMobileLayout,
    ],
  );

  return (
    <ContextBarContext.Provider value={value}>
      {children}
    </ContextBarContext.Provider>
  );
};
